import { submitAudioSubmission, submitTextSubmission } from '../api'
import { getSubmissions, saveSubmission } from '../db'
import { isAuthExpiredError } from '../authErrors'
import { isSubmissionRejectedError } from '../submissionErrors'
import { dispatchUploadProgressEvent } from '../uploadEvents'
import { copy } from '@/copy/strings'
import type { Submission } from '@/types'
import { notifyRecordingChange } from './events'

/**
 * Update submission sync status in IndexedDB
 * Preserves localBlob if it exists
 */
export async function updateSubmissionSyncStatus(
  id: string,
  syncedAt: string,
  localBlob?: Blob,
): Promise<void> {
  const submissions = await getSubmissions()
  const submission = submissions.find((s) => s.id === id)
  if (submission) {
    // Update submission with sync timestamp
    // Preserve localBlob if it exists, or set it if provided
    await saveSubmission({
      ...submission,
      syncedAt: syncedAt,
      localBlob: localBlob || submission.localBlob, // Preserve existing or use new blob
      uploadStatus: 'uploaded',
      processingStatus: submission.processingStatus || 'completed',
      processingUpdatedAt: new Date().toISOString(),
      processingError: null,
    })
    dispatchUploadProgressEvent({ type: 'refresh' })
    notifyRecordingChange()
  }
}

/**
 * Mark a submission as terminally failed (no further retries) and surface a
 * message. Used when the server rejects the submission outright — e.g. the
 * asset's active period has closed — so it isn't retried on every sync.
 */
async function markSubmissionRejected(
  submission: Submission,
  message: string,
): Promise<void> {
  await saveSubmission({
    ...submission,
    uploadStatus: 'failed',
    processingStatus: 'failed',
    processingError: message,
    processingUpdatedAt: new Date().toISOString(),
  })
  dispatchUploadProgressEvent({ type: 'error', message })
  notifyRecordingChange()
}

/**
 * Sync a single submission to the backend
 * Preserves localBlob for audio submissions
 */
export async function syncSubmission(submission: Submission, audioBlob?: Blob): Promise<boolean> {
  try {
    if (submission.type === 'text') {
      const result = await submitTextSubmission(submission)
      await updateSubmissionSyncStatus(submission.id, result.syncedAt)
      return true
    } else if (submission.type === 'audio' && audioBlob) {
      const result = await submitAudioSubmission(submission, audioBlob)
      // Preserve the audio blob locally for playback after sync
      await updateSubmissionSyncStatus(submission.id, result.syncedAt, audioBlob)
      return true
    }
    return false
  } catch (error) {
    // The server permanently refused this submission (e.g. asset window
    // closed). Mark it failed and return false (not throw) so the batch keeps
    // going and we don't re-attempt it forever.
    if (isSubmissionRejectedError(error)) {
      await markSubmissionRejected(submission, copy.submissionWindowClosed)
      return false
    }
    if (isAuthExpiredError(error)) {
      throw error
    }
    console.error(`Failed to sync submission ${submission.id}:`, error)
    return false
  }
}
